import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { appConfigApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SDK = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

function loadAppleSdk() {
  return new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    const existing = document.getElementById("apple-signin-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = "apple-signin-sdk";
    s.src = SDK;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

// "Sign in with Apple" for web. Renders only when a Services ID is configured
// (APPLE_WEB_CLIENT_ID → surfaced via /api/app-config). Uses Apple's JS popup flow,
// which returns an identity token our backend verifies at POST /auth/apple.
export function AppleSignInButton({ onDone }) {
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginWithApple } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    appConfigApi.get().then((c) => setClientId(c?.apple_web_client_id || "")).catch(() => {});
  }, []);

  const signIn = useCallback(async () => {
    setBusy(true);
    try {
      await loadAppleSdk();
      window.AppleID.auth.init({ clientId, scope: "name email", redirectURI: window.location.origin + "/", usePopup: true });
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      if (!idToken) throw new Error("No Apple token");
      const nm = res?.user?.name;
      const full = nm ? [nm.firstName, nm.lastName].filter(Boolean).join(" ") : undefined;
      await loginWithApple(idToken, full);
      toast.success("Signed in with Apple");
      (onDone || (() => navigate("/marketplace", { replace: true })))();
    } catch (err) {
      if (err?.error !== "popup_closed_by_user") toast.error("Apple sign-in failed");
    } finally {
      setBusy(false);
    }
  }, [clientId, loginWithApple, navigate, onDone]);

  if (!clientId) return null;

  return (
    <div className="mt-2">
      <Button type="button" variant="outline" className="w-full gap-2 rounded-full" onClick={signIn} disabled={busy} data-testid="apple-signin">
        <svg width="15" height="15" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
        </svg>
        {busy ? "Signing in…" : "Continue with Apple"}
      </Button>
    </div>
  );
}
